import type {
  ExecutionSlotProjection,
  FeatureFlags,
  GoalProjection,
  ExecutionLeaseProjection,
  ManagerExecutionProjection,
  LogicalAgentProjection,
  PortfolioStatusView,
  ProjectProjection,
  ProjectStatusView,
  QueueEntryProjection,
  WorkflowProjection,
  WorkflowRunProjection,
  ApprovalProjection,
  OperationalContractProjection,
  ScheduleProjection,
  ConnectionProjection,
  KnowledgeDocumentProjection,
  KnowledgeNamespaceProjection,
  BrowserProfileProjection,
  BrowserSessionProjection,
  BrowserArtifactProjection,
  BrowserAuditProjection,
  OpportunityProjection,
  AdvisorReviewProjection,
  AdvisorQuestionProjection,
  AgentPerformanceProjection,
  AdvisorNotificationProjection,
  CreditBalanceProjection,
  CreditLedgerEntryProjection,
  CreditUsageMonthProjection,
} from '@skiphr/contracts'
import { collection, doc, limit, onSnapshot, orderBy, query } from 'firebase/firestore'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { firebaseClient } from '@/firebase'

const foundationFlags: FeatureFlags = {
  managerChat: false,
  billing: false,
  projects: false,
  approvals: false,
  connections: false,
  usage: false,
}

export const useAccountStore = defineStore('accountStore', () => {
  const status = ref<'foundation' | 'ready'>('foundation')
  const featureFlags = ref<FeatureFlags>(foundationFlags)
  return { status, featureFlags }
})


export const useManagerStore = defineStore('managerStore', () => {
  const status = ref<'foundation_preview' | 'ready'>('foundation_preview')
  const paused = ref(false)
  return { status, paused }
})

export const usePortfolioStore = defineStore('portfolioStore', () => {
  const portfolio = ref<PortfolioStatusView | null>(null)
  const attentionCount = computed(() => portfolio.value?.needsUser.length ?? 0)
  const health = computed(() => portfolio.value?.overallHealth ?? 'unknown')
  let unsubscribe: (() => void) | undefined
  function subscribe(uid: string): void {
    unsubscribe?.()
    unsubscribe = onSnapshot(doc(firebaseClient.firestore, 'portfolios', uid), (snapshot) => {
      portfolio.value = snapshot.exists() ? (snapshot.data() as PortfolioStatusView) : null
    })
  }
  function dispose(): void {
    unsubscribe?.()
    unsubscribe = undefined
  }
  return { portfolio, attentionCount, health, subscribe, dispose }
})

export const useProjectStore = defineStore('projectStore', () => {
  const projects = ref<readonly ProjectProjection[]>([])
  const statuses = ref<Readonly<Record<string, ProjectStatusView>>>({})
  const activeProject = computed(() => projects.value[0])
  let unsubscribeProjects: (() => void) | undefined
  let unsubscribeStatuses: (() => void) | undefined
  function subscribe(uid: string): void {
    unsubscribeProjects?.()
    unsubscribeStatuses?.()
    unsubscribeProjects = onSnapshot(
      query(collection(firebaseClient.firestore, 'projects', uid, 'items'), orderBy('updatedAt', 'desc')),
      (snapshot) => { projects.value = snapshot.docs.map((item) => item.data() as ProjectProjection) },
    )
    unsubscribeStatuses = onSnapshot(
      collection(firebaseClient.firestore, 'projectStatuses', uid, 'items'),
      (snapshot) => {
        statuses.value = Object.fromEntries(snapshot.docs.map((item) => [item.id, item.data() as ProjectStatusView]))
      },
    )
  }
  function dispose(): void {
    unsubscribeProjects?.()
    unsubscribeStatuses?.()
  }
  return { projects, statuses, activeProject, subscribe, dispose }
})

export const useGoalStore = defineStore('goalStore', () => {
  const goals = ref<readonly GoalProjection[]>([])
  let unsubscribe: (() => void) | undefined
  function subscribe(uid: string): void {
    unsubscribe?.()
    unsubscribe = onSnapshot(
      query(collection(firebaseClient.firestore, 'goals', uid, 'items'), orderBy('updatedAt', 'desc')),
      (snapshot) => { goals.value = snapshot.docs.map((item) => item.data() as GoalProjection) },
    )
  }
  function dispose(): void { unsubscribe?.() }
  return { goals, subscribe, dispose }
})
export const useAgentStore = defineStore('agentStore', () => {
  const agents = ref<readonly LogicalAgentProjection[]>([])
  let unsubscribe: (() => void) | undefined
  function subscribe(uid: string): void {
    unsubscribe?.()
    unsubscribe = onSnapshot(
      query(collection(firebaseClient.firestore, 'logicalAgents', uid, 'items'), orderBy('updatedAt', 'desc')),
      (snapshot) => { agents.value = snapshot.docs.map((item) => item.data() as LogicalAgentProjection) },
    )
  }
  function dispose(): void { unsubscribe?.(); unsubscribe = undefined }
  return { agents, subscribe, dispose }
})
export const useWorkflowStore = defineStore('workflowStore', () => {
  const workflows = ref<readonly WorkflowProjection[]>([])
  const runs = ref<readonly WorkflowRunProjection[]>([])
  const approvals = ref<readonly ApprovalProjection[]>([])
  const contracts = ref<readonly OperationalContractProjection[]>([])
  const schedules = ref<readonly ScheduleProjection[]>([])
  let unsubscribers: (() => void)[] = []
  function subscribe(uid: string): void {
    dispose()
    const subscribeCollection = <T>(path: string, target: { value: readonly T[] }) =>
      onSnapshot(query(collection(firebaseClient.firestore, path, uid, 'items'), orderBy('updatedAt', 'desc')),
        (snapshot) => { target.value = snapshot.docs.map((item) => item.data() as T) })
    unsubscribers = [
      subscribeCollection('workflows', workflows), subscribeCollection('workflowRuns', runs),
      subscribeCollection('approvals', approvals), subscribeCollection('operationalContracts', contracts),
      subscribeCollection('schedules', schedules),
    ]
  }
  function dispose(): void { unsubscribers.forEach((unsubscribe) => unsubscribe()); unsubscribers = [] }
  return { workflows, runs, approvals, contracts, schedules, subscribe, dispose }
})
export const useExecutionStore = defineStore('executionStore', () => {
  const slots = ref<ExecutionSlotProjection>({
    active: 0,
    entitled: 0,
    runningExecutionIds: [],
    updatedAt: Date.now(),
    source: 'foundation_preview',
  })
  const queue = ref<readonly QueueEntryProjection[]>([])
  const leases = ref<readonly ExecutionLeaseProjection[]>([])
  const executions = ref<readonly ManagerExecutionProjection[]>([])
  let unsubscribers: (() => void)[] = []
  function subscribe(uid: string): void {
    dispose()
    unsubscribers = [
      onSnapshot(doc(firebaseClient.firestore, 'executionSlots', uid), (snapshot) => {
        if (snapshot.exists()) slots.value = snapshot.data() as ExecutionSlotProjection
      }),
      onSnapshot(
        query(collection(firebaseClient.firestore, 'executionQueue', uid, 'items'), orderBy('createdAt', 'desc')),
        (snapshot) => { queue.value = snapshot.docs.map((item) => item.data() as QueueEntryProjection) },
      ),
      onSnapshot(
        query(collection(firebaseClient.firestore, 'executionLeases', uid, 'items'), orderBy('acquiredAt', 'desc')),
        (snapshot) => { leases.value = snapshot.docs.map((item) => item.data() as ExecutionLeaseProjection) },
      ),
      onSnapshot(
        query(collection(firebaseClient.firestore, 'managerExecutions', uid, 'items'), orderBy('updatedAt', 'desc')),
        (snapshot) => { executions.value = snapshot.docs.map((item) => item.data() as ManagerExecutionProjection) },
      ),
    ]
  }
  function dispose(): void {
    unsubscribers.forEach((unsubscribe) => unsubscribe())
    unsubscribers = []
  }
  return { slots, queue, leases, executions, subscribe, dispose }
})
export const useApprovalStore = defineStore('approvalStore', () => ({ pending: ref(0) }))
export const useConnectionStore = defineStore('connectionStore', () => {
  const connections = ref<readonly ConnectionProjection[]>([])
  const required = computed(() => connections.value.filter((item) => item.status === 'requested' || item.status === 'error').length)
  let unsubscribe: (() => void) | undefined
  function subscribe(uid: string): void {
    unsubscribe?.()
    unsubscribe = onSnapshot(
      query(collection(firebaseClient.firestore, 'connections', uid, 'items'), orderBy('updatedAt', 'desc')),
      (snapshot) => { connections.value = snapshot.docs.map((item) => item.data() as ConnectionProjection) },
    )
  }
  function dispose(): void { unsubscribe?.(); unsubscribe = undefined }
  return { connections, required, subscribe, dispose }
})
export const useKnowledgeStore = defineStore('knowledgeStore', () => {
  const namespaces = ref<readonly KnowledgeNamespaceProjection[]>([])
  const documents = ref<readonly KnowledgeDocumentProjection[]>([])
  let unsubscribers: (() => void)[] = []
  function subscribe(uid: string): void {
    dispose()
    unsubscribers = [
      onSnapshot(query(collection(firebaseClient.firestore, 'knowledgeNamespaces', uid, 'items'), orderBy('updatedAt', 'desc')),
        (snapshot) => { namespaces.value = snapshot.docs.map((item) => item.data() as KnowledgeNamespaceProjection) }),
      onSnapshot(query(collection(firebaseClient.firestore, 'knowledgeDocuments', uid, 'items'), orderBy('updatedAt', 'desc')),
        (snapshot) => { documents.value = snapshot.docs.map((item) => item.data() as KnowledgeDocumentProjection) }),
    ]
  }
  function dispose(): void { unsubscribers.forEach((unsubscribe) => unsubscribe()); unsubscribers = [] }
  return { namespaces, documents, subscribe, dispose }
})
export const useBrowserStore = defineStore('browserStore', () => {
  const profiles = ref<readonly BrowserProfileProjection[]>([])
  const sessions = ref<readonly BrowserSessionProjection[]>([])
  const artifacts = ref<readonly BrowserArtifactProjection[]>([])
  const audit = ref<readonly BrowserAuditProjection[]>([])
  let unsubscribers: (() => void)[] = []
  function subscribe(uid: string): void {
    dispose()
    const watch = <T>(path: string, target: { value: readonly T[] }, field = 'updatedAt') =>
      onSnapshot(query(collection(firebaseClient.firestore, path, uid, 'items'), orderBy(field, 'desc')),
        (snapshot) => { target.value = snapshot.docs.map((item) => item.data() as T) })
    unsubscribers = [watch('browserProfiles', profiles), watch('browserSessions', sessions),
      watch('browserArtifacts', artifacts, 'createdAt'), watch('browserAudit', audit, 'createdAt')]
  }
  function dispose(): void { unsubscribers.forEach((unsubscribe) => unsubscribe()); unsubscribers = [] }
  return { profiles, sessions, artifacts, audit, subscribe, dispose }
})
export const useAdvisorStore = defineStore('advisorStore', () => {
  const opportunities = ref<readonly OpportunityProjection[]>([])
  const reviews = ref<readonly AdvisorReviewProjection[]>([])
  const questions = ref<readonly AdvisorQuestionProjection[]>([])
  const performance = ref<readonly AgentPerformanceProjection[]>([])
  const notifications = ref<readonly AdvisorNotificationProjection[]>([])
  const unread = computed(() => notifications.value.filter((item) => !item.read).length)
  let unsubscribers: (() => void)[] = []
  function subscribe(uid: string): void {
    dispose()
    const watch = <T>(path: string, target: { value: readonly T[] }, field: string) =>
      onSnapshot(query(collection(firebaseClient.firestore, path, uid, 'items'), orderBy(field, 'desc')),
        (snapshot) => { target.value = snapshot.docs.map((item) => item.data() as T) })
    unsubscribers = [watch('opportunities', opportunities, 'updatedAt'), watch('advisorReviews', reviews, 'createdAt'),
      watch('advisorQuestions', questions, 'updatedAt'), watch('agentPerformance', performance, 'score'),
      watch('notifications', notifications, 'createdAt')]
  }
  function dispose(): void { unsubscribers.forEach((unsubscribe) => unsubscribe()); unsubscribers = [] }
  return { opportunities, reviews, questions, performance, notifications, unread, subscribe, dispose }
})
export const useNotificationStore = useAdvisorStore
/** Credit balance, this month's usage, and recent purchases and charges. */
export const useUsageStore = defineStore('usageStore', () => {
  const credits = ref<CreditBalanceProjection | null>(null)
  const month = ref<CreditUsageMonthProjection | null>(null)
  const ledger = ref<readonly CreditLedgerEntryProjection[]>([])
  const balance = computed(() => credits.value?.balance ?? 0)
  let unsubscribers: (() => void)[] = []
  function subscribe(uid: string): void {
    dispose()
    const key = new Date().toISOString().slice(0, 7)
    unsubscribers = [
      onSnapshot(doc(firebaseClient.firestore, 'credits', uid), (snapshot) => {
        credits.value = snapshot.exists() ? snapshot.data() as CreditBalanceProjection : null
      }),
      onSnapshot(doc(firebaseClient.firestore, 'creditUsage', uid, 'months', key), (snapshot) => {
        month.value = snapshot.exists() ? snapshot.data() as CreditUsageMonthProjection : null
      }),
      onSnapshot(query(collection(firebaseClient.firestore, 'creditLedger', uid, 'entries'), orderBy('updatedAt', 'desc'), limit(200)),
        (snapshot) => { ledger.value = snapshot.docs.map((item) => item.data() as CreditLedgerEntryProjection) }),
    ]
  }
  function dispose(): void { unsubscribers.forEach((unsubscribe) => unsubscribe()); unsubscribers = [] }
  return { credits, month, ledger, balance, subscribe, dispose }
})
